import {
  Alert,
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { DailyTrendChart } from '@/components/DailyTrendChart';
import { DateRangeFilter } from '@/components/DateRangeFilter';
import { MetricBasisChips } from '@/components/MetricBasisChips';
import { Karta } from '@/components/Karta';
import { NaglowekEkranu } from '@/components/NaglowekEkranu';
import { PasSekcji } from '@/components/PasSekcji';
import { Segmenty } from '@/components/Segmenty';
import { MiniMetryka, PlytaGlowna } from '@/components/PlytaGlowna';
import { EmptyState, ErrorState, LoadingState } from '@/components/states';
import { PrzyciskDowodu, type Dowod } from '@/components/SzufladaDowodu';
import { formatCount, formatGrosze } from '@/lib/format';
import { czyPustyZbior, wartoscDoPokazania } from '@/lib/pustyZbior';
import { useRestaurantSales } from '@/lib/useAnalytics';
import { useDateRange } from '@/lib/useDateRange';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * SPRZEDAŻ RESTAURACJI — migracja do języka „SYGNAŁ" (kierunek C, R5-15-009)
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * PYTANIE EKRANU: ile warta jest oferta sprzedana przez restauracje w tym oknie i która
 * restauracja ciągnie ten wynik. Jedna liczba jest bohaterem, ranking jej służy — wcześniej
 * ekran był płaską tabelą, w której suma okna w ogóle nie padała.
 *
 * ŻADNYCH DANYCH DEMONSTRACYJNYCH: wyłącznie `useRestaurantSales()`, czyli realne
 * `GET /api/crm/analytics/restaurant-sales`. Puste źródło pokazujemy jako pustkę.
 *
 * CO ZMIENIŁO SIĘ W POBIERANIU DANYCH I DLACZEGO
 * Ekran wołał `useRestaurantSales(defaultRange())`. Hak sam czyta wspólne okno z adresu
 * (`useDateRange`), a przekazany argument je NADPISYWAŁ — więc `#/restauracje?from=…&to=…`
 * pokazywało ciche ostatnie 30 dni, a głęboki link z filtrem nie działał na tym jednym ekranie.
 * Teraz okno pochodzi z adresu, tak jak na pozostałych ekranach, i jest sterowalne filtrem.
 */

/**
 * Okno pisane DOSŁOWNIE, ze strzałką, zamiast przez `formatRange()`.
 * Przy oknie jednodniowym `formatRange` zwraca pojedynczą datę, a stan pusty ma obowiązek podać
 * PRZEDZIAŁ, którego brak dotyczy — inaczej „brak" jest nieweryfikowalny.
 */
function oknoDoslownie(from: string, to: string): string {
  return `${from} → ${to}`;
}

export function RestaurantSalesPage() {
  const [range] = useDateRange();
  // Bez argumentu: hak bierze okno z adresu. Patrz nagłówek pliku.
  const { data, isLoading, error } = useRestaurantSales();

  // Okno DEKLAROWANE PRZEZ ODPOWIEDŹ ma pierwszeństwo nad żądanym — backend przycina zakres
  // sam i to jego zakres opisuje pokazane liczby.
  const okno = oknoDoslownie(
    data?.range.date_from ?? range.date_from,
    data?.range.date_to ?? range.date_to,
  );

  const wiersze = data?.restaurants ?? [];
  const zamowienia = data?.summary.total_orders ?? null;

  /*
   * ZERO TO NIE TO SAMO CO PUSTO (lib/pustyZbior.ts).
   * `bytow` musi być `null`, dopóki odpowiedzi nie ma — `0` znaczyłoby „zmierzyliśmy zero
   * restauracji", a my wtedy jeszcze niczego nie zmierzyliśmy.
   */
  const ziarno = { wierszy: zamowienia, bytow: data ? wiersze.length : null };
  const zrodloPuste = czyPustyZbior(ziarno);
  const wartosc = wartoscDoPokazania(data?.summary.total_sales_grosze, ziarno);

  /*
   * ══════════════════════════════════════════════════════════════════════════════════════════
   * PORZĄDEK RANKINGU — sterowanie z makiety, wymiary z kontraktu
   * ══════════════════════════════════════════════════════════════════════════════════════════
   *
   * Makieta ma w nagłówku przełącznik „Uszereguj według" z czterema wymiarami: wartość,
   * dania bez sprzedaży, najsłabsza punktualność, zasięg w szkołach. `RestaurantSalesRow`
   * niesie TRZY pola: `period_sales_grosze`, `period_orders`, `period_days`.
   *
   * Trzech z czterech wymiarów makiety nie da się tu policzyć uczciwie:
   *   • dania bez sprzedaży — to inne ziarno (pozycja katalogu), inny endpoint,
   *   • punktualność        — to dane Drive, nie CRM; tego endpointu w ogóle nie dotyczą,
   *   • zasięg w szkołach   — wymaga złączenia restauracja↔szkoła, którego odpowiedź nie ma.
   * Podstawienie czegokolwiek w te trzy miejsca byłoby rankingiem narysowanym z danych,
   * których nie ma. Zostają więc DWA wymiary, które są prawdziwe — i one wystarczają, żeby
   * przełącznik miał sens, bo odpowiadają na dwa różne pytania: „kto sprzedał najwięcej"
   * i „kto obsłużył najwięcej zamówień". Te kolejności naprawdę się różnią.
   *
   * `period_days` NIE jest wymiarem: to liczba dni kalendarzowych okna, identyczna w każdym
   * wierszu. Sortowanie po niej nie zmieniłoby niczego i sugerowałoby różnicę, której nie ma.
   */
  const PORZADKI = {
    wartosc: {
      etykieta: 'Wartość',
      nadtytul: 'Porządek: wartość katalogowa',
      tytul: 'Ranking wg wartości katalogowej',
      porzadek: (a: typeof wiersze[number], b: typeof wiersze[number]) =>
        b.period_sales_grosze - a.period_sales_grosze,
    },
    zamowienia: {
      etykieta: 'Zamówienia',
      nadtytul: 'Porządek: liczba zamówień',
      tytul: 'Ranking wg liczby zamówień',
      porzadek: (a: typeof wiersze[number], b: typeof wiersze[number]) =>
        b.period_orders - a.period_orders || b.period_sales_grosze - a.period_sales_grosze,
    },
  } as const;

  const [porzadek, setPorzadek] = useState<keyof typeof PORZADKI>('wartosc');
  const wybrany = PORZADKI[porzadek];

  /*
   * Backend sortuje wiersze po priorytecie operacyjnym (spadki najpierw), co jest jeszcze inną
   * kolejnością i innym pytaniem. Sortujemy kopię — to jedyna rzecz, którą warstwa prezentacji
   * tu robi z danymi. Żadna liczba nie jest tu wyliczana od nowa.
   */
  const ranking = [...wiersze].sort(wybrany.porzadek);
  const suma = data?.summary.total_sales_grosze ?? 0;

  /*
   * ZERO SPRZEDAŻY ≠ BRAK DANYCH — i tutaj to rozróżnienie ma konkretną cenę.
   * Lista restauracji NIE pochodzi z okna: backend bierze restauracje z realną sprzedażą
   * operacyjną w bieżącym sezonie (kanon Szybkiego Alertu), a dopiero potem liczy im okno.
   * Restauracja z zerem jest więc ZMIERZONYM zerem — była aktywna w sezonie, a w tym oknie
   * nie sprzedała nic. To jest sygnał biznesowy, nie usterka integracji.
   */
  const bezSprzedazy = wiersze.filter((r) => r.period_sales_grosze === 0 && r.period_orders === 0).length;
  const wszystkieNaZerze = wiersze.length > 0 && bezSprzedazy === wiersze.length;
  const wKatalogu = data?.restaurant_options?.length ?? null;

  const podstawa = { value: 'LIST_VALUE', volume: 'ORDER_COUNT', time: 'DELIVERY_DATE' } as const;

  /*
   * Dowód budowany z TYCH SAMYCH pól, które zasilają liczbę na ekranie. Osobno wpisany opis
   * rozjeżdża się z danymi przy pierwszej zmianie definicji — a wtedy produkt kłamie w jednym
   * z dwóch miejsc i nie wiadomo, w którym.
   */
  const dowodSprzedazy: Dowod = {
    tytul: 'Wartość sprzedaży restauracji (ceny katalogowe)',
    etykietaWartosci: 'Wartość wg cennika w oknie',
    wartosc: wartosc === null ? null : formatGrosze(wartosc),
    podstawa,
    zrodlo: 'CRM /analytics/restaurant-sales → crm_order_items ⋈ crm_transactions (restauracja z POZYCJI zamówienia)',
    // Świeżość źródła sprzedaży jest w całym programie NIEUSTALONA. Stempel odczytu wpisany
    // tutaj byłby zmyśleniem — źródło go nie raportuje.
    klasyfikacja: 'SWIEZOSC_NIEUSTALONA',
    swiezosc: 'NIEUSTALONA — źródło nie raportuje momentu odczytu',
    oknoBiezace: okno,
    // Ten endpoint nie zwraca okna porównawczego w kontrakcie klienta — mówimy to wprost,
    // zamiast pokazywać puste pole, które wygląda jak zapomniane.
    oknoPorownawcze: null,
    wyliczenie: 'Σ (cena katalogowa aktywnej pozycji zamówienia) po restauracjach, okno cięte po dniu dostawy',
    szczegoly: [
      ['Atrybucja do restauracji', 'restauracja z POZYCJI zamówienia, nie z transakcji'],
      ['Pozycje skasowane', 'pominięte (is_deleted)'],
      ['Lista restauracji', 'aktywność operacyjna w bieżącym sezonie — NIE zawężona do okna'],
      ['Restauracje w wyniku', formatCount(data ? wiersze.length : null)],
      ['W tym bez sprzedaży w oknie', formatCount(data ? bezSprzedazy : null)],
      ['Zamówienia (suma po restauracjach)', formatCount(zamowienia)],
    ],
    proba: `${formatCount(data ? wiersze.length : null)} restauracji w oknie`,
    ograniczenia: [
      ...(zrodloPuste
        ? [
            'ŹRÓDŁO ZWRÓCIŁO PUSTY ZBIÓR: zero restauracji i zero zamówień w tym oknie. ' +
              'To NIE jest zmierzone zero — nie ma czego sumować.',
          ]
        : []),
      'Kwota faktycznie pobrana nie jest tu sumowana — źródło nie ma jej na ziarnie pozycji.',
      'Liczba zamówień jest SUMĄ liczb per restauracja: zamówienie z daniami z dwóch restauracji ' +
        'wchodzi do niej dwa razy. Kwota tego problemu nie ma — pozycje dzielą się między restauracje.',
      'Świeżość źródła nieustalona: liczby są poprawne dla momentu odczytu, ale ten moment jest nieznany.',
    ],
    czegoNieWynika:
      'To NIE jest przychód restauracji ani kwota, którą TuSzama jej rozliczyła: sumujemy ceny ' +
      'katalogowe sprzed zniżki szkolnej, a nie pieniądze, które wpłynęły. Nie wynika z tego również ' +
      'nic o marży ani o kosztach dowozu — w tym zbiorze nie ma ani jednej pozycji kosztowej.',
  };

  const dowodBezSprzedazy: Dowod = {
    tytul: 'Restauracje bez sprzedaży w oknie',
    etykietaWartosci: 'Liczba restauracji z zerem',
    wartosc: data ? formatCount(bezSprzedazy) : null,
    podstawa: { value: 'LIST_VALUE', time: 'DELIVERY_DATE' },
    zrodlo: 'CRM /analytics/restaurant-sales → wiersze z period_sales_grosze = 0 i period_orders = 0',
    klasyfikacja: 'SWIEZOSC_NIEUSTALONA',
    swiezosc: 'NIEUSTALONA — źródło nie raportuje momentu odczytu',
    oknoBiezace: okno,
    wyliczenie: 'zliczenie wierszy, w których wartość i liczba zamówień w oknie wynoszą zero',
    szczegoly: [
      ['Skąd wzięła się restauracja na liście', 'realna sprzedaż operacyjna w bieżącym sezonie'],
      ['Restauracje w wyniku', formatCount(data ? wiersze.length : null)],
      ['Restauracje w katalogu (komplet)', formatCount(wKatalogu)],
    ],
    proba: `${formatCount(data ? wiersze.length : null)} restauracji w wyniku`,
    ograniczenia: [
      'Zero dotyczy WYŁĄCZNIE tego okna. Restauracja, która sprzedawała tylko poza oknem, ma tu zero.',
    ],
    czegoNieWynika:
      'Nie wynika z tego, że te restauracje przestały z nami pracować ani że coś się zepsuło: ' +
      'okno bywa krótsze niż ich rytm dostaw, a wakacje i dni wolne zerują sprzedaż całkiem zwyczajnie. ' +
      'Nie wynika też, że to komplet — restauracja, która nie miała sprzedaży operacyjnej w bieżącym ' +
      'sezonie, w ogóle nie trafia na tę listę i nie jest tu policzona.',
  };

  return (
    <Box>
      <NaglowekEkranu
        tytul="Sprzedaż restauracji"
        opis="Ile warta jest oferta sprzedana przez każdą restaurację w wybranym oknie i która restauracja odpowiada za jaką część tego wyniku. Każda liczba prowadzi do swojego wyprowadzenia."
        akcja={<DateRangeFilter wariant="naglowek" />}
      />

      {isLoading ? <LoadingState /> : null}
      {error ? (
        <ErrorState
          error={error}
          context="Control Center czyta wyłącznie API. Gdy backend nie odpowiada albo sesja wygasła, nie pokazujemy liczb zastępczych."
        />
      ) : null}

      {!isLoading && !error ? (
        <Stack spacing={3} className="sygnal-kaskada">
          {/* Ekran dzieli się na NAZWANE DZIAŁY, tak jak w makiecie. Wcześniej był jednym
              stosem bloków o równej wadze — czytelnik nie wiedział, gdzie kończy się „ile",
              a zaczyna „kto". */}
          <PasSekcji
            tytul="Sieć w liczbach"
            opis="Trzy wielkości, od których zaczyna się rozmowa o dostawcach."
          />

          <PlytaGlowna
            kartaId="RESTAURANTS-01"
            // Szuflada dowodu wewnątrz płyty ma WŁASNY adres, osobny od `kartaId` płyty:
            // to dwa różne elementy DOM, a jeden atrybut na obu przestałby rozstrzygać.
            dowodId="RESTAURANTS-14"
            etykieta="Wartość sprzedaży restauracji (ceny katalogowe)"
            wartosc={wartosc === null ? null : formatGrosze(wartosc)}
            opis={
              <Typography variant="body2">
                {zrodloPuste
                  ? 'źródło zwróciło pusty zbiór w tym oknie — to nie jest zmierzone zero, tylko brak danych do policzenia'
                  : 'ceny katalogowe pozycji zamówienia, przed zniżką szkolną — to nie jest kwota pobrana'}
              </Typography>
            }
            zrodlo="CRM /analytics/restaurant-sales → crm_order_items (restauracja z pozycji zamówienia)"
            okno={okno}
            dowod={dowodSprzedazy}
            boczne={
              <>
                <MiniMetryka kartaId="RESTAURANTS-02" wartosc={formatCount(data ? wiersze.length : null)} etykieta="restauracji w wyniku" />
                <MiniMetryka kartaId="RESTAURANTS-03" wartosc={formatCount(zamowienia)} etykieta="zamówień · suma po restauracjach" />
                <MiniMetryka kartaId="RESTAURANTS-04" wartosc={formatCount(data ? bezSprzedazy : null)} etykieta="bez sprzedaży w oknie" />
                <MiniMetryka kartaId="RESTAURANTS-05" wartosc={formatCount(wKatalogu)} etykieta="restauracji w katalogu" />
              </>
            }
          />

          {/* Podstawa metryki stoi PRZY liczbie, nie w przypisie: różnica między wartością
              katalogową a kwotą pobraną jest niejednorodna, więc zmienia RANKING, nie tylko poziom. */}
          <Stack direction="row" spacing={1.5} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <MetricBasisChips basis={podstawa} />
            <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)' }}>
              okno {okno}
            </Typography>
          </Stack>

          {zrodloPuste ? (
            <Alert severity="warning" data-cc-card-id="RESTAURANTS-07">
              Źródło sprzedaży zwróciło pusty zbiór: zero restauracji i zero zamówień w oknie {okno}.
              To jest stan integracji, a nie wynik sprzedaży — nie wolno czytać go jako „nic się nie sprzedało".
            </Alert>
          ) : null}

          {wszystkieNaZerze ? (
            <Alert severity="info" data-cc-card-id="RESTAURANTS-08">
              Wszystkie {formatCount(wiersze.length)} restauracje w wyniku mają w tym oknie zero. To jest
              ZMIERZONE zero, nie brak danych: lista restauracji pochodzi z aktywności w bieżącym sezonie,
              a okno {okno} nie zawiera ani jednej ich sprzedaży.
            </Alert>
          ) : null}

          {data && wiersze.length === 0 ? (
            <EmptyState
              kartaId="RESTAURANTS-09"
              title="Brak sprzedaży w wybranym oknie"
              description={`Okno ${okno}. Źródło nie zwróciło ani jednej restauracji — nie ma czego rankingować ani sumować.`}
            />
          ) : null}

          {data && wiersze.length > 0 ? (
            <>
              {data.charts?.daily_trend?.length ? (
                // Istniejący komponent, nie nowa biblioteka. Tytuł świadomie nie powtarza nazwy
                // ekranu — dwa nagłówki o tej samej treści łamią jednoznaczność ekranu.
                <DailyTrendChart
                  kartaId="RESTAURANTS-10"
                  points={data.charts.daily_trend}
                  title="Dzień po dniu — suma wszystkich restauracji w wyniku"
                />
              ) : null}

              {/* Przełącznik porządku stoi PRZY dziale, którego dotyczy — `PasSekcji` ma slot
                  akcji dokładnie na to. W makiecie siedzi w nagłówku ekranu, ale tam obok stoi
                  już filtr okna; dwa sterowania w jednym rogu konkurowałyby o uwagę, a to
                  dotyczy wyłącznie rankingu, nie całego ekranu. */}
              <PasSekcji
                tytul="Ranking dostawców"
                akcja={
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <Typography variant="overline" sx={{ color: 'var(--tekst-slaby)' }}>
                      Uszereguj według
                    </Typography>
                    <Segmenty
                      etykieta="Porządek rankingu dostawców"
                      wybrana={porzadek}
                      onZmiana={(w) => setPorzadek(w)}
                      opcje={(Object.keys(PORZADKI) as (keyof typeof PORZADKI)[]).map((k) => ({
                        wartosc: k,
                        etykieta: PORZADKI[k].etykieta,
                      }))}
                    />
                  </Stack>
                }
              />

              <Karta
                nadtytul={wybrany.nadtytul}
                tytul={wybrany.tytul}
                akcja={
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <Chip
                      size="small"
                      variant="outlined"
                      className="sygnal-liczba"
                      label={`${formatCount(bezSprzedazy)} bez sprzedaży`}
                      title="Restauracje obecne w wyniku, których wartość i liczba zamówień w tym oknie wynoszą zero."
                    />
                    <PrzyciskDowodu
                      kartaId="RESTAURANTS-15"
                      dowod={dowodBezSprzedazy}
                      etykieta="Dowód zera"
                    />
                  </Stack>
                }
              >
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: '78ch' }}>
                  Wartość katalogowa przypisana po pozycjach zamówienia. Suma tej tabeli nie musi równać
                  się sumie po szkołach — po administracyjnej korekcie pozycji sprzedaż istnieje, ale traci
                  wiarygodną atrybucję. Kolumna „Zamówienia" liczy różne zamówienia W OBRĘBIE restauracji;
                  zamówienie z dwóch restauracji pojawia się w dwóch wierszach.
                </Typography>

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, maxWidth: '78ch' }}>
                  Porządek można zmienić dwoma wymiarami, bo tyle niesie ten odczyt. Makieta oferuje
                  jeszcze trzy — dania bez sprzedaży, punktualność i zasięg w szkołach — a każdy z nich
                  wymaga danych, których ta odpowiedź nie zawiera: innego ziarna, danych dostaw albo
                  złączenia restauracja↔szkoła. Nie sortujemy po wielkościach, których nie zmierzyliśmy.
                </Typography>

                {/* Bez `component={Paper}`: tabela stoi już w karcie, druga rama dawałaby
                    ramkę w ramce — makieta ma kartę bez obramowania, z samym cieniem. */}
                <TableContainer sx={{ overflowX: 'auto' }}>
                  {/* Nazwa dostępna MUSI iść za wybranym porządkiem — inaczej czytnik ekranu
                      ogłasza tabelę „wg wartości katalogowej", kiedy jest uszeregowana inaczej. */}
                  <Table size="small" aria-label={`Ranking restauracji — ${wybrany.tytul.toLowerCase()}`}>
                    <TableHead>
                      {/* Adresy walidacji DOM (R5-15) siedzą na NAGŁÓWKACH kolumn, a nie na
                          komórkach wierszy: karta inwentarza opisuje KOLUMNĘ, a ten sam atrybut
                          powtórzony raz na restaurację przestałby wskazywać jedno miejsce.
                          Atrybut niczego nie zmienia w wyglądzie — jest wyłącznie adresem. */}
                      <TableRow>
                        <TableCell align="right">#</TableCell>
                        <TableCell data-cc-card-id="RESTAURANTS-13">Restauracja</TableCell>
                        <TableCell>Miasto</TableCell>
                        <TableCell align="right" data-cc-card-id="RESTAURANTS-11">Wartość katalogowa</TableCell>
                        <TableCell data-cc-card-id="RESTAURANTS-12">Udział w oknie</TableCell>
                        <TableCell align="right">Zamówienia</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ranking.map((row, i) => {
                        // Udział liczony wyłącznie wtedy, gdy jest od czego liczyć. Dzielenie przez
                        // zero dałoby NaN, a „0%" przy zerowej sumie byłoby wynikiem, którego nie ma.
                        const udzial = suma > 0 ? (row.period_sales_grosze / suma) * 100 : null;
                        const zero = row.period_sales_grosze === 0 && row.period_orders === 0;
                        return (
                          <TableRow key={row.restaurant_id} hover>
                            <TableCell align="right" className="sygnal-liczba" sx={{ color: 'var(--tekst-slaby)' }}>
                              {i + 1}
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                                <span>{row.restaurant_name ?? `#${row.restaurant_id}`}</span>
                                {zero ? (
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    label="bez sprzedaży"
                                    title="Zmierzone zero w tym oknie — restauracja jest na liście, bo miała sprzedaż operacyjną w bieżącym sezonie."
                                  />
                                ) : null}
                              </Stack>
                            </TableCell>
                            <TableCell>{row.city ?? '—'}</TableCell>
                            <TableCell align="right" className="sygnal-liczba">
                              {formatGrosze(row.period_sales_grosze)}
                            </TableCell>
                            <TableCell sx={{ minWidth: 132 }}>
                              {udzial === null ? (
                                <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)' }}>
                                  nie do policzenia
                                </Typography>
                              ) : (
                                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                  <Box
                                    aria-hidden
                                    sx={{
                                      flexGrow: 1,
                                      height: 6,
                                      borderRadius: 'var(--r-pigulka)',
                                      background: 'var(--tlo-glebokie)',
                                      overflow: 'hidden',
                                    }}
                                  >
                                    <Box
                                      className="sygnal-slupek"
                                      sx={{
                                        width: `${Math.max(udzial, 0)}%`,
                                        height: '100%',
                                        borderRadius: 'var(--r-pigulka)',
                                        background: 'var(--zloto)',
                                      }}
                                    />
                                  </Box>
                                  <Typography variant="caption" className="sygnal-liczba" sx={{ minWidth: 40 }}>
                                    {udzial.toLocaleString('pl-PL', { maximumFractionDigits: 1 })}%
                                  </Typography>
                                </Stack>
                              )}
                            </TableCell>
                            <TableCell align="right" className="sygnal-liczba">
                              {formatCount(row.period_orders)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Karta>
            </>
          ) : null}
        </Stack>
      ) : null}
    </Box>
  );
}
